import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useStores } from "../../../../../hooks";
import { locale } from "../../../../../common/consts";
import {
    MenuItemModifier, MenuItemModifierSetChooseManyData,
    MenuItemModifierSetChooseOneData,
    ModifiersSetType
} from "../../../../../services/menu/models";
import { Button, FlexRowSpaceBetween, FlexRowStart, Space, Typography } from "../../../../../common/components/libs";
import NoModifiersInChooseOneSet from "./NoModifiersInChooseOneSet";
import NoModifiersInChooseManySet from "./NoModifiersInChooseManySet";
import EditModifierForm from "./EditModifierForm";
import { Add as AddIcon, Delete as RemoveIcon, Edit as EditIcon } from "@material-ui/icons";
import { Hidden, Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import noImageForProduct from "../../menuEditor/assets/noImageForProduct.png";
import { MenuFormType } from "../../../store/menu/forms";
import IconButton from "@material-ui/core/IconButton";
import { localize } from "../../../../../common/libs";
import RemoveModifierForm from "./RemoveModifierForm";
import {Trans, useTranslation} from "react-i18next";
import { ModifiersFormType } from "../../../store/modifiersSets/forms";


interface ModifierItemProps {
    item: MenuItemModifier;
    onEditButtonClick: () => void;
    onRemoveButtonClick: () => void;
}

const ModifierItem = (props: ModifierItemProps) => {
    const {t} = useTranslation(["general", "menu"]);
    const { item } = props;
    const { constantsStore, generalStore } = useStores();

    const photos = item.data.photosUrls;
    let imageBlockContent = [];
    const imgSrc = photos.length > 0 ? photos[0] : noImageForProduct;
    imageBlockContent.push(
        <div key="noPhoto">
            <img src={imgSrc} alt="" key={imgSrc} style={{
                maxWidth: 150,
                maxHeight: 150,
                borderRadius: 10
            }}/>
        </div>
    );
    const currencyFormatter = constantsStore.getCurrencyFormatter(generalStore.currency.id);



    return <>
        <Hidden smDown>
            <TableRow>
                <TableCell>{localize(item.data.name)} <br/>
                    {
                        item.data.isInStopList && <span
                            style={{
                                display: 'inline-block',
                                color: "#ff0000",
                                fontSize: 14,
                                fontWeight: 400
                            }}
                        >{t("menu:menuEditor.inStopListBracket")}</span>
                    }
                </TableCell>

                <TableCell align="right">{currencyFormatter.toShow(item.data.sellPrice.price)}</TableCell>
                <TableCell align="right">
                    <Button
                        size='small'
                        color="primary"
                        onClick={props.onEditButtonClick}
                    >
                        {t("general:edit")}
                    </Button>
                    <Button
                        size='small'
                        color="secondary"
                        onClick={props.onRemoveButtonClick}
                    >
                        {t("general:delete")}
                    </Button>

                </TableCell>

            </TableRow>
        </Hidden>
        <Hidden mdUp>
            <Typography variant={'h6'} mt={5}>
                {localize(item.data.name)}
                {
                    item.data.isInStopList && <span
                        style={{
                            display: 'inline-block',
                            color: "#ff0000",
                            fontSize: 14,
                            fontWeight: 400
                        }}
                    >{t("menu:menuEditor.inStopListBracket")}</span>
                }
            </Typography>
            <Typography variant={'body1'} mb={2}>
                {t("menu:modifiersSet.price")} {currencyFormatter.toShow(item.data.sellPrice.price)}
            </Typography>
            <Button
                size='small'
                color="primary"
                variant={'outlined'}
                fullWidth
                onClick={props.onEditButtonClick}
            >
                {t("general:edit")}
            </Button>
            <Button
                mt={2}
                size='small'
                color="secondary"
                variant={'outlined'}
                fullWidth
                onClick={props.onRemoveButtonClick}
            >
                {t("general:delete")}
            </Button>
        </Hidden>
    </>
}


interface Props {
    id: number,
    remove: () => void;
}

const EditModifiersSetInline = ({ id, remove }: Props) => {
    const {t} = useTranslation(["general", "menu"]);
    const [name, setName] = useState("");
    const [type, setType] = useState(ModifiersSetType.ChooseOne);
    const [modifiers, setModifiers] = useState<MenuItemModifier[]>([]);
    const { modifiersSetsStore } = useStores();
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [current, setCurrent] = useState<MenuItemModifier | undefined>();
    const [currentIndex, setCurrentIndex] = useState<number | undefined>();
    const [isRemoveModifierFormOpen, setIsRemoveModifierFormOpen] = useState(false);


    const [isLoaded, setIsLoaded] = useState(false);


    const modifiersSet = modifiersSetsStore.getModifiersSetById(id);

    useEffect(() => {
        (async () => {
            await modifiersSetsStore.ensureLoaded();
            const modifiersSet = modifiersSetsStore.getModifiersSetById(id);
            setName(localize(modifiersSet.data.name) || "");
            setType(modifiersSet.data.type);
            setModifiers([...modifiersSet.data.modifiers]);
            setIsLoaded(true)
        })()
    }, [id, modifiersSetsStore.modifiersSets]);

    if (!isLoaded) return null;


    const getSetModifier = (index: number) => (newMenuItemModifier: MenuItemModifier) => {
        const newModifiersList = [...modifiers];
        newModifiersList[index] = newMenuItemModifier;
        setModifiers(newModifiersList);


        //@ts-ignore
        let data: MenuItemModifierSetChooseManyData | MenuItemModifierSetChooseOneData = {
            ...modifiersSet.data,
            name: { ...modifiersSet.data.name, [locale as string]: name },
            modifiers: newModifiersList,
            type,
        }

        modifiersSetsStore.updateModifiersSet({
            menuItemModifierSetId: id,
            data
        })
    };

    const pushNewModifier = (newMenuItemModifier: MenuItemModifier) => {
        const newModifiersList = [...modifiers, newMenuItemModifier];
        setModifiers(newModifiersList);
        //@ts-ignore
        let data: MenuItemModifierSetChooseManyData | MenuItemModifierSetChooseOneData = {
            ...modifiersSet.data,
            name: { ...modifiersSet.data.name, [locale as string]: name },
            modifiers: newModifiersList,
            type,

        }
        modifiersSetsStore.updateModifiersSet({
            menuItemModifierSetId: id,
            data
        })
    };

    const getRemoveModifier = (index: number) => () => {
        const newModifiersList = [...modifiers]
        newModifiersList.splice(index, 1);
        setModifiers(newModifiersList);

        //@ts-ignore
        let data: MenuItemModifierSetChooseManyData | MenuItemModifierSetChooseOneData = {
            name: { [locale as string]: name },
            modifiers: newModifiersList,
            type
        }
        modifiersSetsStore.updateModifiersSet({
            menuItemModifierSetId: id,
            data
        })
    };

    const openForm = () => setIsFormOpen(true);
    const closeForm = () => {
        setIsFormOpen(false);
        setCurrent(undefined);
        setCurrentIndex(-1);
    };

    const openRemoveForm = () => setIsRemoveModifierFormOpen(true);
    const closeRemoveForm = () => {
        setIsRemoveModifierFormOpen(false);
        setCurrent(undefined);
        setCurrentIndex(-1);
    };


    const editButton = <Button color="primary"
                               onClick={() => {
                                   modifiersSetsStore.forms.showPopupForm(ModifiersFormType.editMenuModifiersSet, modifiersSet)
                               }}>
        <EditIcon fontSize='small'/>
        <span
            style={{ display: "inline-block", minWidth: 5 }}
        />
        {t("general:edit")}
    </Button>

    const addOptionButton = <Button color="primary"
                                    onClick={() => {
                                        setCurrent(undefined);
                                        openForm()
                                    }}
    >
        <AddIcon/>
        {t("menu:modifiersSet.addOption")}
    </Button>

    const removeButton = <IconButton color="inherit" onClick={remove}>
        <RemoveIcon color="error"/>
    </IconButton>

    return (
        <div>
            <Hidden smDown>
                <FlexRowSpaceBetween>
                    <FlexRowStart>
                        <div>
                            <Typography variant={"h5"}>
                                {name}
                            </Typography>
                            <Typography variant='body1'>
                                {t("menu:modifiersSet.type")} {t(`menu:modifiersSetType.${type}`)}
                            </Typography>
                        </div>
                    </FlexRowStart>
                    <div>
                        <FlexRowStart>
                            {editButton}
                            {addOptionButton}
                            {removeButton}
                        </FlexRowStart>
                    </div>
                </FlexRowSpaceBetween>
            </Hidden>
            <Hidden mdUp>
                <Typography variant={"h5"}>
                    {name}
                </Typography>
                <Typography variant='body1'>
                    {t("menu:modifiersSet.type")} {t(`menu:modifiersSetType.${type}`)}
                </Typography>
                <Button color="primary"
                        onClick={() => {
                            modifiersSetsStore.forms.showPopupForm(ModifiersFormType.editMenuModifiersSet, modifiersSet)
                        }}
                        mt={2}
                        size={'small'}
                        fullWidth
                        variant={'outlined'}
                >
                    <EditIcon fontSize='small'/>
                    <span
                        style={{ display: "inline-block", minWidth: 5 }}
                    />
                    {t("menu:modifiersSet.editSet")}
                </Button>
                <Button color="primary"
                        mt={2}
                        size={'small'}
                        onClick={() => {
                            setCurrent(undefined);
                            openForm()
                        }}
                        fullWidth
                        variant={'outlined'}
                >
                    <AddIcon/>
                    {t("menu:modifiersSet.addOption")}
                </Button>
                <Button color="primary"
                        mt={2}
                        size={'small'}
                        onClick={remove}
                        fullWidth
                        variant={'outlined'}
                >
                    <RemoveIcon color="error"/>
                    {t("menu:modifiersSet.deleteSet")}
                </Button>
            </Hidden>
            <Space size={2}/>


            <Hidden smDown>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t("general:name")}</TableCell>
                            <TableCell align="right">{t("menu:modifiersSet.otherPrice")}</TableCell>
                            <TableCell align="right">{t("general:actions")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            modifiers.map((item, index) => {
                                return <ModifierItem
                                    item={item}
                                    onRemoveButtonClick={() => {
                                        setCurrent(item);
                                        setCurrentIndex(index);
                                        openRemoveForm()
                                    }}
                                    onEditButtonClick={() => {
                                        setCurrent(item);
                                        setCurrentIndex(index);
                                        openForm()
                                    }}

                                />

                            })
                        }
                    </TableBody>
                </Table>
            </Hidden>

            <Hidden mdUp>
                {
                    modifiers.map((item, index) => {
                        return <ModifierItem
                            item={item}
                            onRemoveButtonClick={() => {
                                setCurrent(item);
                                setCurrentIndex(index);
                                openRemoveForm()
                            }}
                            onEditButtonClick={() => {
                                setCurrent(item);
                                setCurrentIndex(index);
                                openForm()
                            }}

                        />

                    })
                }
            </Hidden>


            {
                modifiers.length === 0 && type === ModifiersSetType.ChooseMany &&
                <NoModifiersInChooseManySet openForm={openForm}/>
            }
            {
                modifiers.length === 0 && type === ModifiersSetType.ChooseOne &&
                <NoModifiersInChooseOneSet openForm={openForm}/>
            }

            <EditModifierForm
                open={isFormOpen}
                current={current}
                updateModifier={getSetModifier(currentIndex)}
                pushNewModifier={pushNewModifier}
                handleDone={closeForm}

            />
            <RemoveModifierForm
                open={isRemoveModifierFormOpen}
                current={current}
                removeModifier={getRemoveModifier(currentIndex)}
                handleDone={closeRemoveForm}
            />
        </div>
    );

};

export default observer(EditModifiersSetInline);
